<script setup>
import { ref, computed, watch, onBeforeUnmount, useAttrs } from 'vue'

import useAnchor, { useAnchorStaticProps } from './composables/useAnchor.js'
import useModelToggle, {
  useModelToggleProps,
  useModelToggleEmits
} from './composables/useModelToggle.js'
import useTransition, {
  useTransitionProps
} from './composables/useTransition.js'
import useTick from './composables/useTick.js'
import useTimeout from './composables/useTimeout.js'
import { addEvt, cleanEvt } from './utils/event.js'
import {
  validatePosition,
  validateOffset,
  setPosition,
  parsePosition
} from './utils/positionEngine.js'
import useScrollTarget from './composables/useScrollTarget.js'
import { useMobile, clickOutside } from '@snowshoe-frontend/utils-component'

const attrs = useAttrs()
const emit = defineEmits([...useModelToggleEmits])
const props = defineProps({
  ...useAnchorStaticProps,
  ...useModelToggleProps,
  ...useTransitionProps,

  maxHeight: {
    type: String,
    default: null
  },
  maxWidth: {
    type: String,
    default: null
  },

  transitionShow: {
    ...useTransitionProps.transitionShow,
    default: 'fade'
  },
  transitionHide: {
    ...useTransitionProps.transitionHide,
    default: 'fade'
  },

  anchor: {
    type: String,
    default: 'bottom middle',
    validator: validatePosition
  },
  self: {
    type: String,
    default: 'top middle',
    validator: validatePosition
  },
  offset: {
    type: Array,
    default: () => [14, 14],
    validator: validateOffset
  },

  scrollTarget: [String],

  delay: {
    type: Number,
    default: 0
  },

  hideDelay: {
    type: Number,
    default: 0
  },

  persistent: Boolean,

  teleportTarget: {
    type: String,
    default: '#teleports'
  }
})

let unwatchPosition, observer

const innerRef = ref(null)
const showing = ref(false)

const isMobile = useMobile('md')

const anchorOrigin = computed(() => parsePosition(props.anchor))
const selfOrigin = computed(() => parsePosition(props.self))
const hideOnRouteChange = computed(() => props.persistent !== true)

const { registerTick, removeTick } = useTick()
const { registerTimeout } = useTimeout()
const { transitionProps, transitionStyle } = useTransition(props)
const { localScrollTarget, changeScrollEvent, unconfigureScrollTarget } =
  useScrollTarget()

const transitionName = computed(() =>
  showing.value ? props.transitionShow : props.transitionHide
)
const { anchorEl, canShow, anchorEvents } = useAnchor({ configureAnchorEl })

const { show, hide } = useModelToggle({
  showing,
  canShow,
  handleShow,
  handleHide,
  hideOnRouteChange,
  processOnMount: true
})

Object.assign(anchorEvents, { delayShow, delayHide })

function handleShow(evt) {
  showing.value = true
  registerTick(() => {
    observer = new MutationObserver(() => updatePosition())
    observer.observe(innerRef.value, {
      attributes: false,
      childList: true,
      characterData: true,
      subtree: true
    })
    updatePosition()
    configureScrollTarget()

    if (isMobile.value) {
      clickOutside.beforeMount(anchorEl.value, { value: handleHide })
    }
  })

  if (unwatchPosition === undefined) {
    unwatchPosition = watch(
      () =>
        window.screen.width +
        '|' +
        window.screen.height +
        '|' +
        props.self +
        '|' +
        props.anchor,
      updatePosition
    )
  }

  registerTimeout(() => {
    emit('show', evt)
  }, props.transitionDuration)
}

function handleHide(evt) {
  removeTick()
  showing.value = false

  anchorCleanup()

  if (isMobile.value) {
    clickOutside.unmounted(anchorEl.value)
  }

  registerTimeout(() => {
    emit('hide', evt)
  }, props.transitionDuration)
}

function configureScrollTarget() {
  if (anchorEl.value !== null) {
    localScrollTarget.value = window

    changeScrollEvent(localScrollTarget.value, hide)
  }
}

function anchorCleanup() {
  if (observer !== undefined) {
    observer.disconnect()
    observer = undefined
  }

  if (unwatchPosition !== undefined) {
    unwatchPosition()
    unwatchPosition = undefined
  }

  unconfigureScrollTarget()

  cleanEvt(anchorEvents, 'tooltipTemp')
}

function updatePosition() {
  setPosition({
    targetEl: innerRef.value,
    offset: props.offset,
    anchorEl: anchorEl.value,
    anchorOrigin: anchorOrigin.value,
    selfOrigin: selfOrigin.value,
    maxHeight: props.maxHeight,
    maxWidth: props.maxWidth
  })
}

function delayShow(evt) {
  registerTimeout(() => {
    show(evt)
  }, props.delay)
}

function delayHide(evt) {
  registerTimeout(() => {
    hide(evt)
  }, props.hideDelay)
}

function configureAnchorEl() {
  if (props.noParentEvent === true || anchorEl.value === null) return
  const evts = isMobile.value
    ? [[anchorEl.value, 'touchstart', 'delayShow', 'passive']]
    : [
        [anchorEl.value, 'mouseenter', 'delayShow', 'passive'],
        [anchorEl.value, 'mouseleave', 'delayHide', 'passive']
      ]

  addEvt(anchorEvents, 'anchor', evts)
}

onBeforeUnmount(anchorCleanup)
</script>

<template>
  <Teleport :to="teleportTarget">
    <transition
      :name="transitionName"
      :appear="transitionProps.appear"
      :appear-active-class="transitionProps.enterActiveClass"
      :leave-active-class="transitionProps.leaveActiveClass"
      :leave-from-class="transitionProps.leaveFromClass"
      :leave-to-class="transitionProps.leaveToClass"
      :enter-active-class="transitionProps.enterActiveClass"
      :enter-from-class="transitionProps.enterFromClass"
      :enter-to-class="transitionProps.enterToClass"
    >
      <div
        v-if="showing"
        ref="innerRef"
        class="tooltip"
        :class="attrs.class"
        :style="transitionStyle"
        role="tooltip"
      >
        <slot></slot>
      </div>
    </transition>
  </Teleport>
</template>

<style lang="scss">
.tooltip {
  z-index: 9000;
  position: fixed !important;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 6px 10px;
}
</style>
